import React, { FunctionComponent, ReactElement, useEffect, useState } from 'react';
import { getUserLocale } from '@empiriecom/module-components/IntlProvider';
import useCookieConsent from '@empiriecom/module-components/hooks/useCookieConsent';
import useConfig from '@empiriecom/module-components/hooks/useConfig';
import { FragmentConfiguration } from '@/config/default';
import { MediaAsset } from '@/src/interfaces';
import { CliplisterResponse } from './types';
import { useProduct } from '../../ProductContext';
import addCliplisterScript from './addCliplisterScript';
import ImageGallery from '../../ImageGallery';

const getCliplisterData = (orderNumber: string): CliplisterResponse => {
  const [response, setResponse] = useState<CliplisterResponse>();
  const {
    externalServices: {
      cliplister: { apiUrl, consentGroup },
    },
  }: FragmentConfiguration = useConfig();

  const isConsentAllowed = useCookieConsent(consentGroup);
  const lang = getUserLocale()?.split('-')[0];

  const body = {
    lang: `${lang},en`,
    requestkey: orderNumber,
  };

  useEffect(() => {
    if (isConsentAllowed) {
      fetch(apiUrl, {
        body: JSON.stringify(body),
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        method: 'POST',
      })
        .then((resp: Response) => {
          if (resp.ok) {
            return resp.json() as Promise<CliplisterResponse>;
          }
          return null;
        })
        .then((data: CliplisterResponse) => {
          setResponse(data);
          return data;
        })
        .catch((err) => {
          // eslint-disable-next-line no-console
          console.error(err);
        });
    }
  }, [orderNumber]);

  return response;
};

export const CliplisterImageGallery: FunctionComponent = (): ReactElement => {
  const {
    product: { images, media, name, orderNumber, sku },
  } = useProduct();
  const {
    externalServices: {
      cliplister: { consentGroup, scriptUrl },
    },
  }: FragmentConfiguration = useConfig();

  const isConsentAllowed = useCookieConsent(consentGroup);

  useEffect(() => addCliplisterScript(isConsentAllowed, scriptUrl), [isConsentAllowed]);

  let mergedImages: MediaAsset[] = [];
  if (media) {
    mergedImages = mergedImages.concat(media);
  } else {
    // TODO: remove when switch to media property is complete
    images.map((image) => mergedImages.push({ type: 'IMAGE', url: image }));
  }

  const cliplisterResponse = getCliplisterData(orderNumber);

  if (
    typeof cliplisterResponse !== 'undefined' &&
    cliplisterResponse !== null &&
    cliplisterResponse.length > 0
  ) {
    // eslint-disable-next-line array-callback-return
    cliplisterResponse[0].videos.map((video, index) => {
      if (index === 0) {
        video.links.map(
          (link) =>
            link.name === 'thumb' && mergedImages.push({ type: 'CLIPLISTER', url: link.location }),
        );
      }
    });
  }

  return (
    <div data-testid="cliplister-gallery">
      <ImageGallery images={mergedImages} name={name} sku={sku} />
    </div>
  );
};

export default CliplisterImageGallery;
